import React, { useCallback, useMemo, useState, Component } from 'react';
import { motion } from 'framer-motion';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { ComposableMap, Geographies, Geography, Marker, Line } from 'react-simple-maps';
import { MapPinIcon, GlobeIcon, PlaneIcon, MapIcon, ZoomInIcon, ZoomOutIcon, MaximizeIcon, Globe2Icon } from 'lucide-react';
interface TravelMapProps {
  language?: 'en' | 'vi';
}
const TravelMap: React.FC<TravelMapProps> = ({
  language = 'en'
}) => {
  const [selectedRegion, setSelectedRegion] = useState<string>('all');
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [hoveredCity, setHoveredCity] = useState<string | null>(null);
  const translations = useMemo(() => ({
    en: {
      title: 'Travel',
      titleHighlight: 'Experience',
      subtitle: 'Countries and Cities Visited',
      allRegions: 'All Regions',
      asia: 'Asia',
      northAmerica: 'North America',
      europe: 'Europe',
      oceania: 'Oceania',
      citiesVisited: 'Cities Visited',
      countriesVisited: 'Countries',
      continents: 'Continents',
      totalDistance: 'Approx. Distance',
      zoomIn: 'Zoom In',
      zoomOut: 'Zoom Out',
      resetZoom: 'Reset Zoom',
      clickCountry: 'Click on a country to see cities visited',
      backToGlobal: 'Back to Global View',
      citiesIn: 'Cities in'
    },
    vi: {
      title: 'Kinh Nghiệm',
      titleHighlight: 'Du Lịch',
      subtitle: 'Quốc Gia và Thành Phố Đã Thăm',
      allRegions: 'Tất Cả Khu Vực',
      asia: 'Châu Á',
      northAmerica: 'Bắc Mỹ',
      europe: 'Châu Âu',
      oceania: 'Châu Đại Dương',
      citiesVisited: 'Thành Phố',
      countriesVisited: 'Quốc Gia',
      continents: 'Châu Lục',
      totalDistance: 'Khoảng Cách Ước Tính',
      zoomIn: 'Phóng To',
      zoomOut: 'Thu Nhỏ',
      resetZoom: 'Đặt Lại Zoom',
      clickCountry: 'Nhấp vào quốc gia để xem thành phố',
      backToGlobal: 'Quay Lại Toàn Cầu',
      citiesIn: 'Thành phố tại'
    }
  }), []);
  const t = translations[language];
  const locations = useMemo(() => ({
    asia: [{
      name: 'Guangdong',
      nameVi: 'Quảng Đông',
      country: 'China',
      coordinates: [113.2644, 23.1291],
      region: 'asia'
    }, {
      name: 'Shanghai',
      nameVi: 'Thượng Hải',
      country: 'China',
      coordinates: [121.4737, 31.2304],
      region: 'asia'
    }, {
      name: 'Hong Kong',
      nameVi: 'Hồng Kông',
      country: 'Hong Kong',
      coordinates: [114.1694, 22.3193],
      region: 'asia'
    }, {
      name: 'Seoul',
      nameVi: 'Seoul',
      country: 'South Korea',
      coordinates: [126.978, 37.5665],
      region: 'asia'
    }, {
      name: 'Tokyo',
      nameVi: 'Tokyo',
      country: 'Japan',
      coordinates: [139.6917, 35.6895],
      region: 'asia'
    }, {
      name: 'Taipei',
      nameVi: 'Đài Bắc',
      country: 'Taiwan',
      coordinates: [121.5654, 25.033],
      region: 'asia'
    }, {
      name: 'Ho Chi Minh City',
      nameVi: 'TP. Hồ Chí Minh',
      country: 'Vietnam',
      coordinates: [106.6297, 10.8231],
      region: 'asia'
    }, {
      name: 'Hanoi',
      nameVi: 'Hà Nội',
      country: 'Vietnam',
      coordinates: [105.8342, 21.0278],
      region: 'asia'
    }, {
      name: 'Hue',
      nameVi: 'Huế',
      country: 'Vietnam',
      coordinates: [107.5909, 16.4637],
      region: 'asia'
    }, {
      name: 'Da Nang',
      nameVi: 'Đà Nẵng',
      country: 'Vietnam',
      coordinates: [108.2022, 16.0544],
      region: 'asia'
    }, {
      name: 'Nha Trang',
      nameVi: 'Nha Trang',
      country: 'Vietnam',
      coordinates: [109.1967, 12.2388],
      region: 'asia'
    }, {
      name: 'Da Lat',
      nameVi: 'Đà Lạt',
      country: 'Vietnam',
      coordinates: [108.4419, 11.9404],
      region: 'asia'
    }, {
      name: 'Phan Thiet',
      nameVi: 'Phan Thiết',
      country: 'Vietnam',
      coordinates: [108.1022, 10.928],
      region: 'asia'
    }, {
      name: 'Mui Ne',
      nameVi: 'Mũi Né',
      country: 'Vietnam',
      coordinates: [108.2833, 10.9333],
      region: 'asia'
    }, {
      name: 'Vung Tau',
      nameVi: 'Vũng Tàu',
      country: 'Vietnam',
      coordinates: [107.0843, 10.346],
      region: 'asia'
    }, {
      name: 'Phu Quoc',
      nameVi: 'Phú Quốc',
      country: 'Vietnam',
      coordinates: [103.965, 10.2899],
      region: 'asia'
    }, {
      name: 'Ca Mau',
      nameVi: 'Cà Mau',
      country: 'Vietnam',
      coordinates: [105.1524, 9.1768],
      region: 'asia'
    }, {
      name: 'Phnom Penh',
      nameVi: 'Phnom Penh',
      country: 'Cambodia',
      coordinates: [104.916, 11.5564],
      region: 'asia'
    }, {
      name: 'Bangkok',
      nameVi: 'Bangkok',
      country: 'Thailand',
      coordinates: [100.5018, 13.7563],
      region: 'asia'
    }, {
      name: 'Naypyidaw',
      nameVi: 'Naypyidaw',
      country: 'Myanmar',
      coordinates: [96.1297, 19.7633],
      region: 'asia'
    }, {
      name: 'Kuala Lumpur',
      nameVi: 'Kuala Lumpur',
      country: 'Malaysia',
      coordinates: [101.6869, 3.139],
      region: 'asia'
    }, {
      name: 'Singapore',
      nameVi: 'Singapore',
      country: 'Singapore',
      coordinates: [103.8198, 1.3521],
      region: 'asia'
    }, {
      name: 'Manila',
      nameVi: 'Manila',
      country: 'Philippines',
      coordinates: [120.9842, 14.5995],
      region: 'asia'
    }],
    northAmerica: [{
      name: 'Boston, MA',
      nameVi: 'Boston, MA',
      country: 'USA',
      coordinates: [-71.0589, 42.3601],
      region: 'northAmerica'
    }, {
      name: 'New York, NY',
      nameVi: 'New York, NY',
      country: 'USA',
      coordinates: [-74.006, 40.7128],
      region: 'northAmerica'
    }, {
      name: 'Philadelphia, PA',
      nameVi: 'Philadelphia, PA',
      country: 'USA',
      coordinates: [-75.1652, 39.9526],
      region: 'northAmerica'
    }, {
      name: 'Washington, DC',
      nameVi: 'Washington, DC',
      country: 'USA',
      coordinates: [-77.0369, 38.9072],
      region: 'northAmerica'
    }, {
      name: 'Virginia',
      nameVi: 'Virginia',
      country: 'USA',
      coordinates: [-78.6569, 37.4316],
      region: 'northAmerica'
    }, {
      name: 'North Carolina',
      nameVi: 'North Carolina',
      country: 'USA',
      coordinates: [-79.0193, 35.7596],
      region: 'northAmerica'
    }, {
      name: 'Maine',
      nameVi: 'Maine',
      country: 'USA',
      coordinates: [-69.4455, 45.2538],
      region: 'northAmerica'
    }, {
      name: 'Vermont',
      nameVi: 'Vermont',
      country: 'USA',
      coordinates: [-72.5778, 44.5588],
      region: 'northAmerica'
    }, {
      name: 'New Hampshire',
      nameVi: 'New Hampshire',
      country: 'USA',
      coordinates: [-71.5724, 43.1939],
      region: 'northAmerica'
    }, {
      name: 'Connecticut',
      nameVi: 'Connecticut',
      country: 'USA',
      coordinates: [-72.7554, 41.6032],
      region: 'northAmerica'
    }, {
      name: 'Rhode Island',
      nameVi: 'Rhode Island',
      country: 'USA',
      coordinates: [-71.4774, 41.5801],
      region: 'northAmerica'
    }, {
      name: 'New Jersey',
      nameVi: 'New Jersey',
      country: 'USA',
      coordinates: [-74.4057, 40.0583],
      region: 'northAmerica'
    }, {
      name: 'Texas',
      nameVi: 'Texas',
      country: 'USA',
      coordinates: [-99.9018, 31.9686],
      region: 'northAmerica'
    }, {
      name: 'California',
      nameVi: 'California',
      country: 'USA',
      coordinates: [-119.4179, 36.7783],
      region: 'northAmerica'
    }, {
      name: 'Nevada',
      nameVi: 'Nevada',
      country: 'USA',
      coordinates: [-116.4194, 38.8026],
      region: 'northAmerica'
    }, {
      name: 'Washington',
      nameVi: 'Washington',
      country: 'USA',
      coordinates: [-120.7401, 47.7511],
      region: 'northAmerica'
    }, {
      name: 'Toronto',
      nameVi: 'Toronto',
      country: 'Canada',
      coordinates: [-79.3832, 43.6532],
      region: 'northAmerica'
    }, {
      name: 'Vancouver',
      nameVi: 'Vancouver',
      country: 'Canada',
      coordinates: [-123.1207, 49.2827],
      region: 'northAmerica'
    }, {
      name: 'Quebec',
      nameVi: 'Quebec',
      country: 'Canada',
      coordinates: [-71.208, 46.8139],
      region: 'northAmerica'
    }],
    europe: [{
      name: 'London',
      nameVi: 'London',
      country: 'UK',
      coordinates: [-0.1276, 51.5074],
      region: 'europe'
    }, {
      name: 'Madrid',
      nameVi: 'Madrid',
      country: 'Spain',
      coordinates: [-3.7038, 40.4168],
      region: 'europe'
    }],
    oceania: [{
      name: 'Sydney',
      nameVi: 'Sydney',
      country: 'Australia',
      coordinates: [151.2093, -33.8688],
      region: 'oceania'
    }]
  }), []);
  const allLocations = useMemo(() => [...locations.asia, ...locations.northAmerica, ...locations.europe, ...locations.oceania], [locations]);
  // Create a mapping of geography names to our country names for better matching
  const countryMapping = useMemo(() => ({
    'United States of America': 'USA',
    'United States': 'USA',
    Korea: 'South Korea',
    'Republic of Korea': 'South Korea',
    'United Kingdom': 'UK',
    'Viet Nam': 'Vietnam',
    Myanmar: 'Myanmar',
    Burma: 'Myanmar'
  }), []);
  const visitedCountries = useMemo(() => new Set(allLocations.map(loc => loc.country)), [allLocations]);
  const isCountryVisited = useCallback((geoName: string) => {
    // Direct match
    if (visitedCountries.has(geoName)) return true;
    // Check mapping
    const mappedName = countryMapping[geoName];
    if (mappedName && visitedCountries.has(mappedName)) return true;
    // Check if any visited country is contained in geo name or vice versa
    for (const country of Array.from(visitedCountries)) {
      if (geoName.includes(country) || country.includes(geoName)) {
        return true;
      }
    }
    return false;
  }, [visitedCountries, countryMapping]);
  const getCountryFromGeo = useCallback((geoName: string) => {
    // Direct match
    if (visitedCountries.has(geoName)) return geoName;
    // Check mapping
    const mappedName = countryMapping[geoName];
    if (mappedName && visitedCountries.has(mappedName)) return mappedName;
    // Check if any visited country matches
    for (const country of Array.from(visitedCountries)) {
      if (geoName.includes(country) || country.includes(geoName)) {
        return country;
      }
    }
    return null;
  }, [visitedCountries, countryMapping]);
  const filteredLocations = useMemo(() => {
    if (selectedCountry) {
      return allLocations.filter(loc => loc.country === selectedCountry);
    }
    return selectedRegion === 'all' ? allLocations : allLocations.filter(loc => loc.region === selectedRegion);
  }, [selectedRegion, selectedCountry, allLocations]);
  const uniqueCountries = useMemo(() => new Set(allLocations.map(loc => loc.country)).size, [allLocations]);
  const countryCenters = useMemo(() => ({
    China: [113, 35],
    'Hong Kong': [114.1694, 22.3193],
    'South Korea': [127.5, 37],
    Japan: [138, 36],
    Taiwan: [121, 24],
    Vietnam: [106, 16],
    Cambodia: [105, 12.5],
    Thailand: [101, 15],
    Myanmar: [96, 21],
    Malaysia: [102, 4],
    Singapore: [103.8198, 1.3521],
    Philippines: [122, 12],
    USA: [-98, 38],
    Canada: [-95, 60],
    UK: [-2, 54],
    Spain: [-4, 40],
    Australia: [133, -27]
  }), []);
  const stats = useMemo(() => [{
    icon: MapPinIcon,
    label: t.citiesVisited,
    value: allLocations.length,
    color: 'from-blue-500 to-cyan-500'
  }, {
    icon: GlobeIcon,
    label: t.countriesVisited,
    value: uniqueCountries,
    color: 'from-green-500 to-emerald-500'
  }, {
    icon: PlaneIcon,
    label: t.continents,
    value: 4,
    color: 'from-purple-500 to-pink-500'
  }, {
    icon: MapIcon,
    label: t.totalDistance,
    value: '~150K km',
    color: 'from-orange-500 to-red-500'
  }], [t, allLocations.length, uniqueCountries]);
  const handleRegionChange = useCallback((region: string) => {
    setSelectedRegion(region);
    setSelectedCountry(null);
  }, []);
  const handleCountryClick = useCallback((countryName: string) => {
    setSelectedCountry(countryName);
  }, []);
  const handleBackToGlobal = useCallback(() => {
    setSelectedCountry(null);
    setSelectedRegion('all');
  }, []);
  const getMapCenter = useMemo(() => {
    if (selectedCountry && countryCenters[selectedCountry]) {
      return countryCenters[selectedCountry];
    }
    if (selectedRegion === 'asia') return [110, 20];
    if (selectedRegion === 'northAmerica') return [-95, 45];
    if (selectedRegion === 'europe') return [10, 50];
    if (selectedRegion === 'oceania') return [135, -25];
    return [20, 20];
  }, [selectedCountry, selectedRegion, countryCenters]);
  const getMapScale = useMemo(() => {
    if (selectedCountry) return 800;
    if (selectedRegion === 'all') return 140;
    return 400;
  }, [selectedCountry, selectedRegion]);
  return <section className="w-full py-12 md:py-24 bg-gradient-to-br from-background via-secondary to-background relative overflow-hidden" data-id="element-1265">
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-1266">
        <div className="absolute top-1/4 -left-48 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" data-id="element-1267" />
        <div className="absolute bottom-1/4 -right-48 w-96 h-96 bg-green-500/10 rounded-full blur-3xl" data-id="element-1268" />
      </div>

      <div className="container mx-auto px-4 relative z-10" data-id="element-1269">
        <div className="text-center mb-8 md:mb-16" data-id="element-1270">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-5xl font-display font-bold mb-4" data-id="element-1271">
            {t.title} <span className="gradient-text" data-id="element-1272">{t.titleHighlight}</span>
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-lg md:text-xl text-muted max-w-2xl mx-auto" data-id="element-1273">
            {t.subtitle}
          </motion.p>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-8 md:mb-12" data-id="element-1274">
          {stats.map((stat, index) => {
          const Icon = stat.icon;
          return <div key={index} className="bg-secondary/50 backdrop-blur-sm rounded-2xl p-4 md:p-6 border border-white/10 text-center" data-id="element-1275">
                <div className={`w-10 h-10 md:w-12 md:h-12 mx-auto mb-3 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center`} data-id="element-1276">
                  <Icon size={20} className="text-white" data-id="element-1277" />
                </div>
                <div className="text-2xl md:text-3xl font-bold mb-1" data-id="element-1278">
                  {typeof stat.value === 'number' ? stat.value : stat.value}
                </div>
                <div className="text-xs md:text-sm text-muted" data-id="element-1279">
                  {stat.label}
                </div>
              </div>;
        })}
        </motion.div>

        {!selectedCountry && <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="flex flex-wrap justify-center gap-2 md:gap-3 mb-6 md:mb-8" data-id="element-1280">
            {[{
          key: 'all',
          label: t.allRegions
        }, {
          key: 'asia',
          label: t.asia
        }, {
          key: 'northAmerica',
          label: t.northAmerica
        }, {
          key: 'europe',
          label: t.europe
        }, {
          key: 'oceania',
          label: t.oceania
        }].map(region => <button key={region.key} onClick={() => handleRegionChange(region.key)} className={`px-4 md:px-6 py-2 md:py-3 rounded-xl font-medium transition-all text-sm ${selectedRegion === region.key ? 'bg-gradient-to-r from-blue-600 to-cyan-500 text-white shadow-lg' : 'bg-secondary/50 text-muted hover:bg-secondary border border-white/10'}`} data-id="element-1281">
                {region.label}
              </button>)}
          </motion.div>}

        {selectedCountry && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4" data-id="element-1282">
            <h3 className="text-xl md:text-2xl font-bold" data-id="element-1283">
              {t.citiesIn} {selectedCountry}
            </h3>
            <button onClick={handleBackToGlobal} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-xl hover:shadow-lg transition-all" data-id="element-1284">
              <Globe2Icon size={18} data-id="element-1285" />
              {t.backToGlobal}
            </button>
          </motion.div>}

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-secondary/30 rounded-2xl p-4 md:p-8 border border-white/10 mb-6 md:mb-8" data-id="element-1286">
          <TransformWrapper initialScale={1} minScale={0.5} maxScale={4} centerOnInit data-id="element-1287">
            {({
            zoomIn,
            zoomOut,
            resetTransform
          }) => <>
                <div className="flex flex-wrap gap-2 mb-4 justify-end" data-id="element-1288">
                  <button onClick={() => zoomIn()} className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" title={t.zoomIn} data-id="element-1289">
                    <ZoomInIcon size={18} data-id="element-1290" />
                  </button>
                  <button onClick={() => zoomOut()} className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" title={t.zoomOut} data-id="element-1291">
                    <ZoomOutIcon size={18} data-id="element-1292" />
                  </button>
                  <button onClick={() => resetTransform()} className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" title={t.resetZoom} data-id="element-1293">
                    <MaximizeIcon size={18} data-id="element-1294" />
                  </button>
                  {(selectedCountry || selectedRegion !== 'all') && <button onClick={handleBackToGlobal} className="px-3 py-2 bg-gradient-to-r from-green-600 to-emerald-500 text-white rounded-lg hover:shadow-lg transition-all flex items-center gap-2" title={t.backToGlobal} data-id="element-1295">
                      <Globe2Icon size={18} data-id="element-1296" />
                      <span className="hidden sm:inline text-sm" data-id="element-1297">
                        {t.backToGlobal}
                      </span>
                    </button>}
                </div>

                <TransformComponent wrapperStyle={{
              width: '100%',
              height: '400px',
              cursor: 'grab'
            }} contentStyle={{
              width: '100%',
              height: '100%'
            }} data-id="element-1298">
                  <div className="w-full h-[400px] md:h-[600px] bg-slate-900/50 rounded-xl overflow-hidden" data-id="element-1299">
                    <ComposableMap projection="geoMercator" projectionConfig={{
                  scale: getMapScale,
                  center: getMapCenter
                }} style={{
                  width: '100%',
                  height: '100%'
                }} data-id="element-1300">
                      <Geographies geography="https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json" data-id="element-1301">
                        {({
                      geographies
                    }) => geographies.map(geo => {
                      const isVisited = isCountryVisited(geo.properties.name);
                      const countryName = getCountryFromGeo(geo.properties.name);
                      return <Geography key={geo.rsmKey} geography={geo} fill={isVisited ? '#1e40af' : '#1e293b'} stroke="#334155" strokeWidth={0.5} onClick={() => {
                        if (isVisited && !selectedCountry && countryName) {
                          handleCountryClick(countryName);
                        }
                      }} style={{
                        default: {
                          outline: 'none',
                          cursor: isVisited ? 'pointer' : 'default'
                        },
                        hover: {
                          outline: 'none',
                          fill: isVisited ? '#2563eb' : '#334155'
                        },
                        pressed: {
                          outline: 'none'
                        }
                      }} data-id="element-1302" />;
                    })}
                      </Geographies>

                      {filteredLocations.map((location, i) => {
                    if (i === 0) return null;
                    const prev = filteredLocations[i - 1];
                    return <Line key={`line-${i}`} from={prev.coordinates} to={location.coordinates} stroke="#3b82f6" strokeWidth={1} strokeLinecap="round" strokeOpacity={0.3} data-id="element-1303" />;
                  })}

                      {filteredLocations.map((location, i) => <Marker key={`marker-${i}`} coordinates={location.coordinates} onMouseEnter={() => setHoveredCity(location.name)} onMouseLeave={() => setHoveredCity(null)} data-id="element-1304">
                          <circle r={hoveredCity === location.name ? 8 : 5} fill="#10b981" stroke="#fff" strokeWidth={2} className="transition-all duration-200 cursor-pointer" style={{
                      filter: 'drop-shadow(0 0 8px rgba(16, 185, 129, 0.6))'
                    }} data-id="element-1305" />
                          {hoveredCity === location.name && <text textAnchor="middle" y={-15} style={{
                      fontFamily: 'system-ui',
                      fontSize: '12px',
                      fill: '#fff',
                      fontWeight: 'bold',
                      pointerEvents: 'none'
                    }} data-id="element-1306">
                              {language === 'vi' ? location.nameVi : location.name}
                            </text>}
                        </Marker>)}
                    </ComposableMap>
                  </div>
                </TransformComponent>
              </>}
          </TransformWrapper>

          <p className="text-xs md:text-sm text-muted text-center mt-4" data-id="element-1307">
            {selectedCountry ? 'Showing cities visited' : t.clickCountry}
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1308">
          {Object.entries(locations).map(([region, cities]) => {
          if (selectedRegion !== 'all' && selectedRegion !== region) return null;
          return <div key={region} className="bg-secondary/30 rounded-xl p-4 md:p-6 border border-white/10" data-id="element-1309">
                <h3 className="text-lg md:text-xl font-bold mb-4 capitalize flex items-center gap-2" data-id="element-1310">
                  <div className="w-2 h-2 rounded-full bg-green-500" data-id="element-1311" />
                  {region === 'asia' ? t.asia : region === 'northAmerica' ? t.northAmerica : region === 'europe' ? t.europe : t.oceania}
                </h3>
                <div className="space-y-2 max-h-[300px] overflow-y-auto" data-id="element-1312">
                  {cities.map((city, i) => <div key={i} className="flex items-center gap-2 text-xs md:text-sm text-muted hover:text-primary transition-colors" data-id="element-1313">
                      <MapPinIcon size={14} className="text-green-500 flex-shrink-0" data-id="element-1314" />
                      <span data-id="element-1315">
                        {language === 'vi' ? city.nameVi : city.name},{' '}
                        {city.country}
                      </span>
                    </div>)}
                </div>
              </div>;
        })}
        </motion.div>
      </div>
    </section>;
};
export default TravelMap;